/**
 * Distributes the given multipliers across a wheel of a specified size in a patterned manner.
 *
 * The function sorts the input multipliers in ascending order and then fills the wheel as follows:
 * - For every index divisible by 3, it assigns the smallest multiplier.
 * - For every index where the remainder is 1 when divided by 3, it assigns the second smallest multiplier.
 * - For every index where the remainder is 2 when divided by 3, it assigns the largest remaining multipliers in reverse order.
 *
 * If the first and last elements of the resulting array are the same, the first two elements are swapped to ensure variety.
 *
 * @param params - An object containing:
 *   @param multipliers - An array of numbers representing the multipliers to be distributed.
 *   @param wheelSize - The total number of slots on the wheel.
 * @returns An array of numbers representing the multipliers spread across the wheel.
 *
 * @example
 * // Example usage:
 * // spreadMultipliersOnWheel({ multipliers: [10, 25, 1000, 5, 100, 3.5, 2], wheelSize: 16 })
 * // returns:
 * // [2, 3.5, 1000, 2, 3.5, 100, 2, 3.5, 25, 2, 3.5, 10, 2, 3.5, 5, 3.5]
 */
export function spreadMultipliersOnWheel({
    multipliers,
    wheelSize,
}: {
    multipliers: number[];
    wheelSize: number;
}): number[] {
    const sortedMultipliers = multipliers.toSorted((a, b) => a - b);
    const spreadMultipliers = new Array(wheelSize).fill(0);

    let reverseIndex = 0;
    //Fill the array first with the first 2 multipliers
    for (let i = 0; i < wheelSize; i++) {
        if (i % 3 === 0) {
            spreadMultipliers[i] = sortedMultipliers[0];
        } else if (i % 3 === 1) {
            spreadMultipliers[i] = sortedMultipliers[1];
        } else {
            reverseIndex++;
            spreadMultipliers[i] = sortedMultipliers[sortedMultipliers.length - reverseIndex];
        }
    }
    //If the first and last elements are the same, swap the first two elements
    if (spreadMultipliers[0] === spreadMultipliers[spreadMultipliers.length - 1]) {
        const temp = spreadMultipliers[0];
        spreadMultipliers[0] = spreadMultipliers[1];
        spreadMultipliers[1] = temp;
    }
    return spreadMultipliers;
}
